import { MouseEventHandler, useMemo, useRef, useState } from 'react'
import { ColDef } from '@ag-grid-community/core'
import { useClickOutsideAndEscape } from 'hooks'

import { Button } from 'components/common'

import {
  SettingsIcon,
  ExportIcon,
  FilterIcon,
  ChevronIcon,
} from 'components/icons'

import ColumnManager from './columnManager'
import SearchBar from './searchBar'
import DataTableImportButton from './dataTableImportButton'
import { ActiveColumnKey } from 'types/dataTable'

type Props = {
  columns: ColDef[]
  showFilter: boolean
  isEditor: boolean
  handleFilter?: MouseEventHandler<HTMLButtonElement>
  handleImport?: (data: any) => Promise<any>
  handleExport?: MouseEventHandler<HTMLButtonElement>
  activeColumnKey: ActiveColumnKey
  placeholder?: string
  defaultSelectedColumns?: string[]
  loading?: boolean
}

const DataTableController = ({
  columns,
  showFilter,
  isEditor,
  handleFilter,
  handleImport,
  handleExport,
  activeColumnKey,
  placeholder,
  defaultSelectedColumns,
  loading,
}: Props) => {
  const [showColumnMenu, setShowColumnMenu] = useState(false)

  const ref = useRef<HTMLDivElement | null>(null)
  useClickOutsideAndEscape({ ref, callback: () => setShowColumnMenu(false) })

  // filter the column which have set hidden in column def
  const filteredColumns = useMemo(
    () => columns.filter((col) => col?.hide !== false),
    [columns],
  )

  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      {/* Search bar */}
      <SearchBar placeholder={placeholder} disabled={loading || showFilter} />

      {/* Table Actions */}
      <div className="flex flex-wrap items-center gap-1">
        <div ref={ref}>
          <Button
            type="button"
            buttonText="Columns"
            className={`rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200 ${
              showColumnMenu ? 'bg-gray-200' : ''
            }`}
            variant="custom"
            onClick={() => !loading && setShowColumnMenu(!showColumnMenu)}
            icon={<SettingsIcon />}
            iconClassName="!h-5 !w-5 text-dataTableIcon"
            icon2={<ChevronIcon />}
            icon2ClassName="!h-2.5 !w-2.5"
            disabled={loading}
          />
          {/* Column Manager modal */}
          {showColumnMenu && (
            <ColumnManager
              columns={filteredColumns}
              setShowColumnMenu={setShowColumnMenu}
              activeColumnKey={activeColumnKey}
              defaultSelectedColumns={defaultSelectedColumns}
            />
          )}
        </div>
        <Button
          buttonText="Filters"
          className={`rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary ${
            showFilter && 'bg-gray-200'
          } hover:bg-gray-200`}
          variant="custom"
          icon={<FilterIcon />}
          iconClassName="text-dataTableIcon"
          onClick={handleFilter}
          disabled={loading}
        />
        <Button
          type="button"
          buttonText="Export"
          variant="custom"
          iconClassName="!h-5 !w-5 text-dataTableIcon"
          icon={<ExportIcon />}
          className="rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
          onClick={handleExport}
          disabled={loading}
        />
        {isEditor && handleImport && (
          <DataTableImportButton
            disabled={loading}
            uploadApiFunction={handleImport}
          />
        )}
      </div>
    </div>
  )
}

export default DataTableController
